// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InjectCss from "@insite/mobius/utilities/injectCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { getMainNavigationWidget } from "@werner/App/Store/Selectors/WidgetSelectors";
import { color, layoutContainer, xScreen } from "@werner/XDL/Utilities";
import * as React from "react";
import styled, { css } from "styled-components";

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    searchInputSection?: InjectableCss;
    headerActionSection?: InjectableCss;
    headerSearchMobileSection?: InjectableCss;
}

const SectionWrapper = styled(StyledWrapper)`
    display: flex;
    align-items: center;

    ${InjectCss}
`;

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${layoutContainer}

            padding-top: 34px;
            padding-bottom: 34px;
            position: relative;
            background: ${color("common.white")};

            ${xScreen("lte", "md")} {
                padding-top: 20px;
                padding-bottom: 20px;
                z-index: 10;
            }
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            display: flex;
            align-items: center;
            margin-right: 80px;
            gap: 16px;
            ${xScreen("lte", "sm")} {
                margin-right: 0;
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            flex-grow: 1;
            max-width: 484px;
            ${xScreen("lte", "md")} {
                max-width: unset;
            }
            ${xScreen("lte", "sm")} {
                max-width: unset;
                width: 100%;
            }
        `,
    },
    linkListHidden: {
        below: "lg",
    },
    searchInputSection: {
        css: css`
            margin-right: 50px;
            display: flex;
            flex-grow: 1;
            ${xScreen("lte", "sm")} {
                margin-right: 0;
            }
        `,
    },
    headerActionSection: {
        css: css`
            min-width: 290px;
            ${xScreen("lte", "md")} {
                min-width: unset;
            }
        `,
    },
    headerSearchMobileSection: {
        css: css`
            height: 0;
            transition: all 500ms ease-in-out;
            ${({ showSearchSectionOnMobile }: any) =>
                showSearchSectionOnMobile &&
                css`
                    height: auto;
                `};
            .header-search-inner-wrapper {
                ${layoutContainer}
                width: 100%;
                background: ${color("common.ui5")};
                padding-top: 8px;
                padding-bottom: 8px;
                transition: all 500ms ease-in-out;
                opacity: 0;
                z-index: 1;
                pointer-events: none;
                ${({ showSearchSectionOnMobile }: any) =>
                    showSearchSectionOnMobile &&
                    css`
                        opacity: 1;
                        pointer-events: unset;
                    `};
            }
        `,
    },
};

const styles = logoLinksSearchStyles;
const LogoLinksSearch: React.FC<PageProps> = ({ id }) => {
    const { mainNavigation, isTabletScreen, isMobileScreen, showSearchSectionOnMobile } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        mainNavigation: getMainNavigationWidget(state),
        showSearchSectionOnMobile: (state as CustomAppState).xHeader.showSearchSectionOnMobile,
    }));

    // XNG-Changes: change structure
    return (
        <>
            <StyledWrapper {...styles.container}>
                <SectionWrapper {...styles.searchInputSection} data-test-selector="search-input-section">
                    <StyledWrapper {...styles.logoWrapper}>
                        {isTabletScreen &&
                            createWidgetElement("Header/MainNavigation", {
                                fields: { ...mainNavigation },
                            })}
                        <XngZone contentId={id} zoneName="Logo" hint="Logo Zone" />
                    </StyledWrapper>
                    {!isMobileScreen && (
                        <StyledWrapper {...styles.searchMenuWrapper}>
                            <Zone contentId={id} zoneName="Search" />
                        </StyledWrapper>
                    )}
                </SectionWrapper>
                <SectionWrapper {...styles.headerActionSection} data-test-selector="header-actions-section">
                    <XngZone contentId={id} zoneName="HeaderActions" hint="HeaderActions Zone" />
                </SectionWrapper>
            </StyledWrapper>
            {isMobileScreen && (
                <StyledWrapper {...styles.headerSearchMobileSection} {...{ showSearchSectionOnMobile }}>
                    <SectionWrapper
                        data-test-selector="header-search-mobile-section"
                        className="header-search-inner-wrapper"
                    >
                        <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                            <Zone contentId={id} zoneName="Search" />
                        </StyledWrapper>
                    </SectionWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const logoLinksSearch: WidgetModule = {
    component: LogoLinksSearch,
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
